import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { FaTrash, FaEdit, FaEye } from 'react-icons/fa';

const BACKEND = process.env.REACT_APP_BACKEND;

const ServicesList = () => {
  const [serviceData, setServiceData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [originalVideoUrl, setOriginalVideoUrl] = useState('');

  const fetchServices = async () => {
    try {
      const res = await getRequest('services');
      setServiceData(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const openModal = (service, viewOnly = false) => {
    setCurrentService(service);
    setIsViewMode(viewOnly);
    setImageFile(null);
    const initialVideoUrl = service?.videoUrl ?? '';
    setVideoUrlInput(initialVideoUrl);
    setOriginalVideoUrl(initialVideoUrl);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!currentService?.id) return alert('Missing service ID!');

    const formData = new FormData();
    formData.append('title', currentService.title || '');
    formData.append('description', currentService.description || '');
    formData.append('status', currentService.status || 'active');
    const cleanedVideoUrl = videoUrlInput.trim();
    if (cleanedVideoUrl) {
      formData.append('videoUrl', cleanedVideoUrl);
    } else if (originalVideoUrl?.trim()) {
      formData.append('videoUrl', '');
    }
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      await patchRequest(`services/${currentService.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchServices();
      setShowModal(false);
    } catch (err) {
      console.error('Failed to update service:', err?.response?.data || err);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await deleteRequest(`services/${id}`);
      fetchServices();
    } catch (err) {
      console.error('Failed to delete service:', err);
    }
  };

  const tableHeaders = [
    {
      Header: 'Image',
      accessor: 'image',
      Cell: ({ row }) =>
        row.original.image ? (
          <img
            src={`${BACKEND}uploads/${row.original.image}`}
            alt="service"
            width={50}
            height={50}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span>No Image</span>
        )
    },
    { Header: 'Title', accessor: 'title' },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => <TruncatedMessage text={row?.original?.description} maxLength={10} />
    },
    { Header: 'Status', accessor: 'status' },
    {
      Header: 'Video',
      accessor: 'videoUrl',
      Cell: ({ row }) =>
        row.original.videoUrl ? (
          <a href={row.original.videoUrl} target="_blank" rel="noreferrer">
            View
          </a>
        ) : (
          <span className="text-muted">—</span>
        )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#17a2b8' }}
            onClick={() => openModal(row.original, true)}
          >
            <FaEye />
          </button>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#ffc107' }}
            onClick={() => openModal(row.original, false)}
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#dc3545' }}
            onClick={() => handleRemove(row.original.id)}
          >
            <FaTrash />
          </button>
        </>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Services List</h4>
        </div>
        <div className="mx-4">
          {serviceData?.length ? (
            <AdminTable tableHeaders={tableHeaders} tableData={serviceData} />
          ) : (
            <p>No services available.</p>
          )}
        </div>
      </div>

      {showModal && currentService && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isViewMode ? 'See All Details' : 'Update Service'}</h5>
                <button className="close" onClick={() => setShowModal(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body">
                {isViewMode ? (
                  <>
                    {['title', 'description', 'status', 'videoUrl'].map((field) => (
                      <div className="form-group mb-2" key={field}>
                        <label htmlFor={field} className="admin-label">
                          {field.replace('_', ' ')}
                        </label>
                        <input
                          id={field}
                          type="text"
                          className="form-control"
                          value={currentService[field] || ''}
                          readOnly
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="title" className="admin-label">Title</label>
                        <input
                          id="title"
                          type="text"
                          className="form-control"
                          value={currentService.title || ''}
                          onChange={(e) => setCurrentService({ ...currentService, title: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="status" className="admin-label">Status</label>
                        <select
                          id="status"
                          className="form-control"
                          value={currentService.status || 'active'}
                          onChange={(e) => setCurrentService({ ...currentService, status: e.target.value })}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="videoUrl" className="admin-label">Video URL (Optional)</label>
                        <input
                          id="videoUrl"
                          type="url"
                          className="form-control"
                          value={videoUrlInput}
                          onChange={(e) => setVideoUrlInput(e.target.value)}
                          placeholder="Enter video link (optional)"
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="description" className="admin-label">Description</label>
                        <textarea
                          id="description"
                          rows={3}
                          className="form-control"
                          value={currentService.description || ''}
                          onChange={(e) => setCurrentService({ ...currentService, description: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2">
                      <label htmlFor="service-image" className="admin-label">Image</label>
                      <input
                        id="service-image"
                        type="file"
                        className="form-control"
                        onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                      />

                      <div className="mt-2">
                        {imageFile ? (
                          <img
                            src={URL.createObjectURL(imageFile)}
                            width={60}
                            height={60}
                            className="border rounded"
                            alt="preview"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : currentService.image ? (
                          <img
                            src={`${BACKEND}uploads/${currentService.image}`}
                            width={60}
                            height={60}
                            className="border rounded"
                            alt="current"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <span className="text-muted">No preview</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                {!isViewMode && (
                  <button className="btn addbtn" onClick={handleSave}>
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServicesList;
